/**
 * A floating window the user can drag by its header.
 *
 * Four windows in the moderator area wrote this out for themselves, and all
 * four were broken in the same way: they found the header with
 * `closest('.voice-monitor-header')`, a name CSS Modules renames at build time,
 * so the lookup returned null and nothing ever dragged. The handle is marked
 * with `data-drag-handle` here, which no build rewrites.
 *
 * FloatingWindow (floating-window.tsx) is built on it, and a window wants that
 * rather than this. Spread `dragHandleProps` on the header and `ref` on the
 * window, and give the window `position: fixed` in its sheet, placed by where
 * the drag put it:
 *
 *     const { position, isDragging, windowRef, dragHandleProps } =
 *       useDraggableWindow({ initialPosition: { x: 340, y: 20 } });
 *     const place = { '--window-x': `${position.x}px`, '--window-y': `${position.y}px` } as CSSProperties;
 *     <div ref={windowRef} className={styles.window} style={place}>
 *       <div {...dragHandleProps}>…</div>
 *     </div>
 *
 * A button inside the handle is never a drag: every one of the four had to
 * exclude its own close and clear buttons by name, which is the same rule. The
 * one exception is the grip (`data-grip`), the handle's own button, which
 * drags and moves the window from the keyboard (`moveBy`).
 *
 * The window is held inside the screen — dragged, moved from the keyboard,
 * as it opens (a place kept from a larger screen) and whenever the screen
 * shrinks — as the game client's FloatingWindow is.
 */

import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

export interface DraggableWindowInput {
  initialPosition: { x: number; y: number };
  /** Called with each new position, for windows that remember where they were. */
  onPositionChange?: (position: { x: number; y: number }) => void;
}

interface Point {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

/**
 * Where a window of `size` may stand on a screen of `viewport`, nearest to
 * `at`: all of it on the screen, or its top left corner on it when it is
 * larger than the screen.
 */
export function clampWindowPosition(at: Point, size: Size, viewport: Size): Point {
  return {
    x: Math.max(0, Math.min(at.x, viewport.width - size.width)),
    y: Math.max(0, Math.min(at.y, viewport.height - size.height)),
  };
}

const screenSize = (): Size => ({ width: window.innerWidth, height: window.innerHeight });

/** Where this window may stand on the screen as it is now, nearest to `at`: measured, then held inside. */
const heldInside = (element: HTMLElement, at: Point): Point =>
  clampWindowPosition(at, { width: element.offsetWidth, height: element.offsetHeight }, screenSize());

export function useDraggableWindow({ initialPosition, onPositionChange }: DraggableWindowInput) {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const windowRef = useRef<HTMLDivElement>(null);

  /** The window where `at` asks, held inside the screen by its own size. */
  const place = (at: Point) => {
    const element = windowRef.current;
    if (!element) return;
    const next = heldInside(element, at);
    setPosition(next);
    onPositionChange?.(next);
  };

  const handleMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) return;
    // A control inside the header is a click, not a grab — but the grip.
    if (target.closest('button:not([data-grip])')) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const element = windowRef.current;
      if (!element) return;
      const next = heldInside(element, { x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
      setPosition(next);
      onPositionChange?.(next);
    };
    const handleMouseUp = () => setIsDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onPositionChange]);

  // Held inside the screen as it opens — before it is first painted — and
  // whenever the screen shrinks. Read from where it is drawn, so a window not
  // moved since keeps its place until the screen takes it away.
  useLayoutEffect(() => {
    const keepInside = () => {
      const element = windowRef.current;
      if (!element) return;
      const box = element.getBoundingClientRect();
      const inside = clampWindowPosition({ x: box.left, y: box.top }, box, screenSize());
      if (Math.abs(inside.x - box.left) < 0.5 && Math.abs(inside.y - box.top) < 0.5) return;
      setPosition(inside);
      onPositionChange?.(inside);
    };
    keepInside();
    window.addEventListener('resize', keepInside);
    return () => window.removeEventListener('resize', keepInside);
  }, [onPositionChange]);

  return {
    position,
    isDragging,
    windowRef,
    /** Goes on the header element that should be grabbable. */
    dragHandleProps: { 'data-drag-handle': true as const, onMouseDown: handleMouseDown },
    /** Moves the window by so much, held inside the screen: the grip's keys. */
    moveBy: (dx: number, dy: number) => place({ x: position.x + dx, y: position.y + dy }),
  };
}
